/**
 * 货币相关工具函数
 */

import type { CurrencyInfo } from '@/types'

/**
 * 货币符号映射
 */
const CURRENCY_SYMBOLS: Record<string, string> = {
  CNY: '¥',
  USD: '$',
  HKD: 'HK$',
  EUR: '€',
  JPY: '¥',
  SGD: 'S$'
}

/**
 * 货币名称映射
 */
const CURRENCY_NAMES: Record<string, string> = {
  CNY: '人民币',
  USD: '美元',
  HKD: '港币',
  EUR: '欧元',
  JPY: '日元',
  SGD: '新加坡元'
}

/**
 * 获取货币符号
 * @param currencyCode 货币代码
 * @returns 货币符号
 */
export function getCurrencySymbol(currencyCode: string): string {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode
}

/**
 * 获取货币名称
 * @param currencyCode 货币代码
 * @returns 货币名称
 */
export function getCurrencyName(currencyCode: string): string {
  return CURRENCY_NAMES[currencyCode] || currencyCode
}

/**
 * 格式化金额
 * @param amount 金额
 * @param currency 货币代码
 * @param precision 小数位数，默认2位
 * @param showSymbol 是否显示货币符号，默认true
 * @returns 格式化后的金额字符串
 */
export function formatCurrency(
  amount: number,
  currency: string = 'CNY',
  precision: number = 2,
  showSymbol: boolean = true
): string {
  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })
  
  if (showSymbol) {
    const symbol = getCurrencySymbol(currency)
    return `${symbol}${formatted}`
  }
  
  return formatted
}

/**
 * 格式化金额（紧凑显示，大数字使用万、亿等单位）
 * @param amount 金额
 * @param currency 货币代码
 * @returns 格式化后的金额字符串
 */
export function formatCurrencyCompact(amount: number, currency: string = 'CNY'): string {
  const symbol = getCurrencySymbol(currency)
  
  if (amount >= 100000000) {
    // 大于等于1亿
    return `${symbol}${(amount / 100000000).toFixed(2)}亿`
  } else if (amount >= 10000) {
    // 大于等于1万
    return `${symbol}${(amount / 10000).toFixed(2)}万`
  } else {
    return formatCurrency(amount, currency, 2)
  }
}

/**
 * 解析金额字符串（移除货币符号和格式）
 * @param amountStr 金额字符串
 * @returns 数字金额
 */
export function parseCurrencyAmount(amountStr: string): number {
  // 移除所有非数字、非小数点、非负号的字符
  const cleaned = amountStr.replace(/[^\d.-]/g, '')
  const amount = parseFloat(cleaned)
  return isNaN(amount) ? 0 : amount
}

/**
 * 货币转换
 * @param amount 金额
 * @param rate 汇率
 * @returns 转换后的金额
 */
export function convertCurrency(amount: number, rate: number): number {
  return amount * rate
}

/**
 * 获取货币显示文本
 * @param currency 货币信息
 * @returns 显示文本，格式：符号 代码 - 名称
 */
export function getCurrencyDisplayText(currency: CurrencyInfo): string {
  return `${currency.symbol} ${currency.code} - ${currency.name}`
}

/**
 * 检查货币代码是否有效
 * @param currencyCode 货币代码
 * @returns 是否有效
 */
export function isValidCurrency(currencyCode: string): boolean {
  return currencyCode in CURRENCY_SYMBOLS
}

